// Fronted/src/components/Home/SermonSection.js
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { FaCompress, FaExpand, FaPause, FaPlay, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { useUpdates } from "../../contexts/updatesContext";

const ACCESS_TOKEN = "EAAW2xrVlunoBOxH3OXOLqBTO5srLvspu61oWnSTchgIhprATMPp71lziGyEmMiLM6seDEQZBsaPAc6Rwmx46mwUkhwlwzUER8SqnVEJzCokkxUZCH9p1L2cEp1EUZBCR8ssQf2rbnbUWX6JyzMMcfZAv0o1h090ZCLZB2gTtiNo3rJfs5SnFnoEvRbDyCrTbxhZBX9wrCZAZBP4md3EjjHNmErrdmyVIbGO1ZAH4cvZCzWZB";
const PAGE_ID = "203411489522199";

const SermonSection = () => {
  const { updates, currentUpdate } = useUpdates();

  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const videoRef = useRef(null);

  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const playerRef = useRef(null);


  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
        setShowControls(true);
        setShowInfo(false);
        setVideoEnded(false);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
        setShowControls(false);
        setShowInfo(true);
      }
    }
  };

  const handleVideoClick = () => {
    if (isPlaying) {
      togglePlay(); // Pause if playing
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (playerRef.current.requestFullscreen) {
        playerRef.current.requestFullscreen();
      } else if (playerRef.current.webkitRequestFullscreen) {
        playerRef.current.webkitRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowControls(false);
    setShowInfo(true);
    setVideoEnded(true);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!videoRef.current) return;
      
      switch (e.key) {
        case " ":
          e.preventDefault();
          togglePlay();
          break;
        case "m":
          toggleMute();
          break;
        case "f":
          toggleFullscreen();
          break;
        case "ArrowRight":
          videoRef.current.currentTime += 5;
          break;
        case "ArrowLeft":
          videoRef.current.currentTime -= 5;
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <section className="w-full px-4 md:px-6 py-6 md:py-8">
      <motion.div 
        className="w-full bg-white shadow-lg rounded-lg overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div 
          id={currentUpdate.id} 
          key={currentUpdate.id} 
          className="relative"
          ref={playerRef}
        >
          {/* Video element */}
          {updates.video_url ? (
            <video
              className="w-full rounded-lg cursor-pointer"
              ref={videoRef}
              onClick={handleVideoClick}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnd}
              //poster={currentUpdate.thumbnail}
              muted={isMuted}
            >
              <source src={currentUpdate.video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <video
              className="w-full rounded-lg cursor-pointer"
              ref={videoRef}
              onClick={handleVideoClick}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnd}
              muted={isMuted}
            >
              <source src={currentUpdate.video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}

          {/* Info section - shown when video is not playing */}
          <AnimatePresence>
            {showInfo && (
              <motion.div
                className={`absolute bottom-4 left-4 right-4 md:bottom-8 md:left-8 md:right-8 p-4`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="flex">
                  <div className="bg-primary text-white text-xs md:text-sm px-3 py-1 rounded-full">
                    {currentUpdate.type || "Sermon"}
                  </div>
                </div>
                <p className="text-white text-lg md:text-2xl font-bold mt-2">
                  {currentUpdate.title}
                </p>
                <div className="flex mt-4 space-x-2 md:space-x-4">
                  <button
                    className="bg-primary text-white px-4 py-2 rounded-md text-sm md:text-base hover:bg-primary/80 transition-colors h-10 flex items-center"
                    onClick={togglePlay}
                  >
                    {videoEnded ? (
                      <>
                        <FaPlay className="mr-2" /> Watch Again
                      </>
                    ) : isPlaying ? (
                      <>
                        <FaPause className="mr-2" /> Pause
                      </>
                    ) : (
                      <>
                        <FaPlay className="mr-2" /> Play
                      </>
                    )}
                  </button>
                  <a href="https://www.youtube.com/@excellentloveministrieslep5467/videos">
                  <button className="bg-gray-200 text-black px-4 py-2 rounded-md text-sm md:text-base hover:bg-gray-300 transition-colors h-10">
                    More
                  </button>
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Custom controls - shown when video is playing */}
          <AnimatePresence>
            {showControls && isPlaying && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-between p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Top controls */}
                <div className="flex justify-between items-start">
                 
                  <button 
                    onClick={toggleFullscreen}
                    className="text-white p-2 rounded-full hover:bg-white/20 transition-colors"
                  >
                    {isFullscreen ? <FaCompress /> : <FaExpand />}
                  </button>
                </div>

                {/* Bottom controls */}
                <div className="mt-auto">
                  {/* Progress bar */}
                  <div 
                    className="w-full h-2 bg-white/30 rounded-full mb-2 cursor-pointer"
                    onClick={handleProgressClick}
                  >
                    <div 
                      className="h-full bg-primary rounded-full relative"
                      style={{ width: `${progress}%` }}
                    >
                      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex space-x-4">
                      <button
                        onClick={togglePlay}
                        className="text-white hover:text-primary transition-colors"
                      >
                        <FaPause size={20} />
                      </button>
                      <button
                        onClick={toggleMute}
                        className="text-white hover:text-primary transition-colors"
                      >
                        {isMuted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
                      </button>
                      <span className="text-white text-sm">
                        {videoRef.current ? formatTime(videoRef.current.currentTime) : '0:00'} / 
                        {videoRef.current ? formatTime(videoRef.current.duration) : '0:00'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Play button overlay - shown when paused but not showing info */}
          {!showInfo && !showControls && !isPlaying && (
            <motion.div 
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button
                onClick={togglePlay}
                className="bg-primary/90 text-white p-4 rounded-full hover:bg-primary transition-colors"
              >
                <FaPlay size={24} />
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </section>
  );
};

export default SermonSection;